import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, HeartIcon, ShieldIcon, BrainIcon, HomeIcon, SparklesIcon, CheckCircle2Icon, XCircleIcon, LockIcon, GiftIcon, FileTextIcon, ZapIcon, CompassIcon, MessageCircleIcon, UsersIcon, TrendingUpIcon, EyeIcon, BookOpenIcon } from 'lucide-react';
const emergencias = [{
  title: 'Aislamiento extremo',
  detail: '(días sin salir del cuarto)',
  meaning: 'Posible depresión o evento traumático no procesado'
}, {
  title: 'Cambios bruscos de conducta',
  detail: 'sin explicación',
  meaning: 'Señal de alerta que requiere evaluación profesional'
}, {
  title: 'Autolesiones',
  detail: '(cortes, golpes, quemaduras)',
  meaning: 'Sufrimiento intenso, nunca manipulación'
}, {
  title: 'Hablar de no querer vivir',
  detail: '',
  meaning: 'Siempre debe tomarse en serio. Sin excepción.'
}, {
  title: 'Ataques de pánico o ansiedad severa',
  detail: '',
  meaning: 'El sistema nervioso está desbordado — no lo controla'
}, {
  title: 'Consumo de sustancias como escape',
  detail: '',
  meaning: 'Busca alivio de un dolor que no sabe nombrar'
}];
const protocoloCalma = [{
  letter: 'C',
  word: 'Contén tu propia emoción',
  detail: 'Respira primero. Si tú estás en pánico, tu hijo lo siente y se cierra más.'
}, {
  letter: 'A',
  word: 'Acércate sin irrumpir',
  detail: 'Golpea antes de entrar. Siéntate cerca, no enfrente.'
}, {
  letter: 'L',
  word: 'Limita juicios y preguntas',
  detail: 'Cero "¿por qué hiciste eso?" en el momento agudo. Solo presencia.'
}, {
  letter: 'M',
  word: 'Mapea el nivel de riesgo',
  detail: 'Pregunta directo: "¿Estás pensando en hacerte daño?" preguntar alivia, no planta la idea.'
}, {
  letter: 'A',
  word: 'Activa los recursos correctos',
  detail: 'Un abrazo, una conversación, o una llamada al médico según lo que detectes.'
}];
const frasesNunca = [{
  bad: '"Eso no es para tanto."',
  good: '"Veo que estás sufriendo mucho. Eso es real."'
}, {
  bad: '"¿Por qué no me dijiste antes?"',
  good: '"Me alegra que me lo estés diciendo ahora."'
}, {
  bad: '"Piensa en todo lo que tienes."',
  good: '"Ahora mismo solo necesito saber cómo te sientes tú."'
}, {
  bad: '"Con lo que me costó criarte..."',
  good: '"Nada de lo que sientes es una carga para mí."'
}];
const incluyeBono1 = ['Qué hacer después de la crisis para fortalecer el vínculo (no lo que la mayoría de padres hace — que sin quererlo empeora todo)', 'Cómo crear un plan de seguridad conjunto con tu hijo antes de que llegue la próxima tormenta', 'El ejercicio de "Tu Plan de Emergencia Personal" para que actúes con calma cuando todo a tu alrededor sea caos'];
const pilares = [{
  icon: HomeIcon,
  name: 'Pertenencia',
  what: 'La certeza de que existe al menos un lugar donde es aceptado sin condiciones',
  how: 'Ese lugar debe ser tu hogar — y depende de ti'
}, {
  icon: TrendingUpIcon,
  name: 'Competencia',
  what: 'La experiencia de poder resolver problemas y superar desafíos',
  how: 'Nace de los retos bien calibrados, no de la sobreprotección'
}, {
  icon: SparklesIcon,
  name: 'Autonomía',
  what: 'La percepción de que sus decisiones tienen peso real',
  how: 'Nace del respeto genuino a su voz dentro de la familia'
}];
const mitos = [{
  myth: '"El amor propio se gana siendo el mejor."',
  truth: 'La autoestima saludable es incondicional — no se gana ni se pierde con los resultados'
}, {
  myth: '"Si le digo que está bien como es, no se esforzará."',
  truth: 'La validación incondicional aumenta la motivación intrínseca, no la reduce'
}, {
  myth: '"Hablar de emociones lo hace más débil."',
  truth: 'Nombrar emociones activa la corteza prefrontal y reduce la intensidad emocional (Lieberman, 2007)'
}, {
  myth: '"La autoestima alta es narcisismo."',
  truth: 'El narcisismo nace de la autoestima frágil — son procesos opuestos'
}];
const amenazas = [{
  icon: EyeIcon,
  title: 'Las redes sociales y la comparación constante',
  body: 'Instagram y TikTok presentan versiones curadas e irreales de la vida de otros. Tu hijo se compara permanentemente con estándares que no existen en la realidad. La investigadora Jean Twenge documentó que el aumento del uso de redes sociales desde 2012 correlaciona directamente con el aumento de ansiedad y depresión adolescente.'
}, {
  icon: ZapIcon,
  title: 'La cultura del rendimiento y el perfeccionismo',
  body: 'El perfeccionismo no es ambición. Es miedo al rechazo disfrazado de excelencia. Y cuando la autoestima depende de los resultados, el primer fracaso puede derrumbarlo todo.'
}, {
  icon: UsersIcon,
  title: 'La soledad emocional en la generación más "conectada"',
  body: 'La generación más conectada tecnológicamente es también la más sola emocionalmente. Las amistades digitales no reemplazan el contacto que el cerebro adolescente necesita para regularse.'
}];
const afirmaciones = ['"No tienes que ser perfecto para merecer mi amor."', '"Veo el esfuerzo que estás poniendo, aunque el resultado no sea el esperado."', '"Tu opinión importa en esta familia."', '"Está bien no estar bien. Puedes decírmelo."', '"No tienes que ganarte un lugar aquí. Ya lo tienes."', '"Equivocarse es parte de aprender. Yo también lo hago."'];
const senalesAlerta = [{
  signal: 'Tristeza o irritabilidad la mayoría de los días',
  when: 'Si dura más de 2 semanas → consulta profesional'
}, {
  signal: 'Caída importante en rendimiento escolar sin causa aparente',
  when: 'Si dura más de 3-4 semanas'
}, {
  signal: 'Cambios marcados en sueño o apetito',
  when: 'Si persiste más de 2 semanas'
}, {
  signal: 'Comentarios negativos frecuentes sobre sí mismo',
  when: 'Desde el primer momento — conversa y evalúa'
}, {
  signal: 'Abandono de actividades que antes disfrutaba',
  when: 'Si se combina con otras señales → actúa'
}];
const incluyeBono2 = ['La Auditoría del Amor Propio en tu Hogar — un ejercicio de reflexión honesta que revela qué ambiente emocional estás creando sin saberlo', 'Cómo hablar de ti mismo delante de tu hijo para modelar el amor propio — porque lo que él te ve hacer pesa mil veces más que lo que le dices', 'Cómo distinguir entre el comportamiento y la persona — la diferencia entre frases que construyen y frases que sin querer destruyen'];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
export function BonusSection() {
  return <section className="bg-[#FAFAF7] pt-24 pb-8 px-5" data-id="element-25">
      <div className="max-w-5xl mx-auto" data-id="element-26">
        {/* Headline de transición */}
        <motion.div {...fadeUp} className="text-center mb-12" data-id="element-27">
          <div className="inline-flex items-center gap-2 bg-[#F4A535]/15 text-[#B5701A] text-xs font-bold tracking-[3px] uppercase px-4 py-2 rounded-full" data-id="element-28">
            <GiftIcon className="w-4 h-4" data-id="element-29" />
            Bonos exclusivos incluidos
          </div>
        </motion.div>

        {/* ============ BONO #1 ============ */}
        <motion.div {...fadeUp} className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-[#E2E8F0] overflow-hidden mb-20" data-id="element-30">
          <div className="bg-gradient-to-br from-[#1A2744] to-[#2D3E6B] text-white p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center" data-id="element-31">
            <div data-id="element-32">
              <div className="inline-flex items-center gap-2 bg-[#F4A535] text-[#1A2744] text-xs font-bold tracking-[2px] uppercase px-4 py-2 rounded-full mb-5" data-id="element-33">
                <GiftIcon className="w-4 h-4" data-id="element-34" />
                Bono #1
              </div>
              <h3 className="text-3xl md:text-4xl font-bold leading-tight mb-3" data-id="element-35">
                Emergencias Emocionales en la Adolescencia
              </h3>
              <p className="text-[#B8C8E8] text-base md:text-lg leading-relaxed" data-id="element-36">
                Guía de acción rápida para padres: cómo reconocer, responder y
                acompañar a tu hijo en sus momentos más difíciles.
              </p>
            </div>
            <div className="flex justify-center" data-id="element-37">
              <img src="https://cdn.magicpatterns.com/uploads/s4RzZZMuhsGQojF6N6GZN4/Bono_1.png" alt="Bono 1 — Emergencias Emocionales en la Adolescencia" className="max-w-full h-auto max-h-[360px] drop-shadow-2xl" data-id="element-38" />
            </div>
          </div>

          <div className="p-8 md:p-12" data-id="element-39">
            {/* Datos del bono */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 bg-[#FAFAF7] rounded-2xl p-6 border border-[#E2E8F0]" data-id="element-40">
              <div data-id="element-41">
                <div className="flex items-center gap-2 text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-42">
                  <FileTextIcon className="w-4 h-4 text-[#F4A535]" data-id="element-43" /> Formato
                </div>
                <p className="text-sm font-semibold text-[#1A2744]" data-id="element-44">
                  Guía digital (PDF)
                </p>
              </div>
              <div data-id="element-45">
                <div className="flex items-center gap-2 text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-46">
                  <ZapIcon className="w-4 h-4 text-[#F4A535]" data-id="element-47" /> Acceso
                </div>
                <p className="text-sm font-semibold text-[#1A2744]" data-id="element-48">
                  Inmediato
                </p>
              </div>
              <div data-id="element-49">
                <div className="text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-50">
                  Valor real
                </div>
                <p className="text-sm font-semibold text-[#A0AEC0] line-through" data-id="element-51">
                  $16 USD
                </p>
              </div>
              <div data-id="element-52">
                <div className="text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-53">
                  Tu precio
                </div>
                <p className="text-sm font-extrabold text-[#38A169]" data-id="element-54">GRATIS</p>
              </div>
            </div>

            {/* Protocolo CALMA */}
            <div className="mb-12 bg-gradient-to-br from-[#F0F4F0] to-[#FAFAF7] rounded-2xl p-6 md:p-8 border border-[#38A169]/20" data-id="element-55">
              <div className="flex items-start gap-3 mb-5" data-id="element-56">
                <CompassIcon className="w-6 h-6 text-[#38A169] flex-shrink-0 mt-1" data-id="element-57" />
                <div data-id="element-58">
                  <h5 className="text-lg md:text-xl font-bold text-[#1A2744] mb-1" data-id="element-59">
                    El Protocolo CALMA
                  </h5>
                  <p className="text-sm text-[#4A5568]" data-id="element-60">
                    5 pasos para actuar con cabeza fría cuando el corazón está
                    en pánico. Basado en los Primeros Auxilios Psicológicos de
                    la OMS adaptado para padres sin formación clínica.
                  </p>
                </div>
              </div>
              <div className="space-y-3" data-id="element-61">
                {protocoloCalma.map((step, i) => <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-4 border border-[#E2E8F0]" data-id="element-62">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#38A169] text-white text-xl font-black flex items-center justify-center" data-id="element-63">
                      {step.letter}
                    </div>
                    <div data-id="element-64">
                      <p className="font-bold text-[#1A2744] text-sm md:text-base" data-id="element-65">
                        {step.word}
                      </p>
                      <p className="text-sm text-[#4A5568] mt-1" data-id="element-66">
                        {step.detail}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <blockquote className="border-l-4 border-[#F4A535] pl-5 py-2 italic text-[#1A2744] text-base md:text-lg" data-id="element-67">
              "No puedes evitar todas las tormentas que vivirá tu hijo. Pero sí
              puedes ser el refugio al que siempre puede regresar."
            </blockquote>
          </div>
        </motion.div>

        {/* ============ BONO #2 ============ */}
        <motion.div {...fadeUp} className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-[#E2E8F0] overflow-hidden" data-id="element-68">
          <div className="bg-gradient-to-br from-[#7C5C6F] to-[#A57C8E] text-white p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center" data-id="element-69">
            <div data-id="element-70">
              <div className="inline-flex items-center gap-2 bg-[#F4A535] text-[#1A2744] text-xs font-bold tracking-[2px] uppercase px-4 py-2 rounded-full mb-5" data-id="element-71">
                <GiftIcon className="w-4 h-4" data-id="element-72" />
                Bono #2
              </div>
              <h3 className="text-3xl md:text-4xl font-bold leading-tight mb-3" data-id="element-73">
                Amor Propio y Salud Mental en la Adolescencia
              </h3>
              <p className="text-white/80 text-base md:text-lg leading-relaxed" data-id="element-74">
                Cómo cultivar la autoestima saludable de tu hijo, proteger su
                bienestar emocional y modelar el amor propio desde casa.
              </p>
            </div>
            <div className="flex justify-center" data-id="element-75">
              <img src="https://cdn.magicpatterns.com/uploads/pfraPYWpm5jadMB8XQtPVa/Bono_2.png" alt="Bono 2 — Amor Propio y Salud Mental en la Adolescencia" className="max-w-full h-auto max-h-[360px] drop-shadow-2xl" data-id="element-76" />
            </div>
          </div>

          <div className="p-8 md:p-12" data-id="element-77">
            {/* Datos del bono */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 bg-[#FAFAF7] rounded-2xl p-6 border border-[#E2E8F0]" data-id="element-78">
              <div data-id="element-79">
                <div className="flex items-center gap-2 text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-80">
                  <FileTextIcon className="w-4 h-4 text-[#F4A535]" data-id="element-81" /> Formato
                </div>
                <p className="text-sm font-semibold text-[#1A2744]" data-id="element-82">
                  Guía digital (PDF)
                </p>
              </div>
              <div data-id="element-83">
                <div className="flex items-center gap-2 text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-84">
                  <ZapIcon className="w-4 h-4 text-[#F4A535]" data-id="element-85" /> Acceso
                </div>
                <p className="text-sm font-semibold text-[#1A2744]" data-id="element-86">
                  Inmediato
                </p>
              </div>
              <div data-id="element-87">
                <div className="text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-88">
                  Valor real
                </div>
                <p className="text-sm font-semibold text-[#A0AEC0] line-through" data-id="element-89">
                  $16 USD
                </p>
              </div>
              <div data-id="element-90">
                <div className="text-xs font-bold tracking-wide uppercase text-[#718096] mb-1" data-id="element-91">
                  Tu precio
                </div>
                <p className="text-sm font-extrabold text-[#38A169]" data-id="element-92">GRATIS</p>
              </div>
            </div>

            {/* Las 3 amenazas */}
            <div className="mb-12" data-id="element-93">
              <div className="flex items-center gap-2 mb-5" data-id="element-94">
                <AlertTriangleIcon className="w-5 h-5 text-[#F4A535]" data-id="element-95" />
                <h5 className="text-base md:text-lg font-bold text-[#1A2744] uppercase tracking-wide" data-id="element-96">
                  Las 3 amenazas silenciosas que atacan su autoestima ahora
                  mismo
                </h5>
              </div>
              <div className="space-y-4" data-id="element-97">
                {amenazas.map((a, i) => {
                const Icon = a.icon;
                return <div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 flex gap-4" data-id="element-98">
                      <div className="flex-shrink-0" data-id="element-99">
                        <div className="w-12 h-12 rounded-xl bg-[#F4A535]/15 flex items-center justify-center" data-id="element-100">
                          <Icon className="w-6 h-6 text-[#B5701A]" data-id="element-101" />
                        </div>
                        <div className="text-center mt-2 text-2xl font-black text-[#F4A535]" data-id="element-102">
                          {i + 1}
                        </div>
                      </div>
                      <div data-id="element-103">
                        <h6 className="font-bold text-[#1A2744] text-base md:text-lg mb-2" data-id="element-104">
                          {a.title}
                        </h6>
                        <p className="text-sm md:text-base text-[#4A5568] leading-relaxed" data-id="element-105">
                          {a.body}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}